import { useEffect, useRef, useState, useCallback } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize, Subtitles, ArrowRight, RotateCcw } from "lucide-react";
import { CAPTIONS, NEXT_VIDEO_URL } from "../captions";

const formatTime = (s) => {
  if (!Number.isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const getActiveCaption = (t) => {
  for (let i = 0; i < CAPTIONS.length; i++) {
    if (t >= CAPTIONS[i].start && t <= CAPTIONS[i].end) return CAPTIONS[i].text;
  }
  return "";
};

export default function FerumPlayer() {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const hideControlsTimer = useRef(null);

  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [ended, setEnded] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [ccOn, setCcOn] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [loadError, setLoadError] = useState("");

  // Derived caption — re-evaluates whenever currentTime updates
  const activeCaption = getActiveCaption(currentTime);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (ended) {
      v.currentTime = 0;
      setEnded(false);
    }
    if (v.paused) {
      const p = v.play();
      if (p && typeof p.then === "function") {
        p.catch((err) => {
          // eslint-disable-next-line no-console
          console.error("Video play failed:", err);
          setLoadError(err?.message || "Unable to play video.");
        });
      }
    } else {
      v.pause();
    }
  }, [ended]);

  const handleTimeUpdate = () => {
    if (videoRef.current) setCurrentTime(videoRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) setDuration(videoRef.current.duration);
  };

  const handleSeek = (e) => {
    const v = videoRef.current;
    if (!v || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    v.currentTime = Math.max(0, Math.min(duration, pct * duration));
    setEnded(false);
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const handleVolume = (e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    if (videoRef.current) {
      videoRef.current.volume = val;
      videoRef.current.muted = val === 0;
      setMuted(val === 0);
    }
  };

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const handleEnded = () => {
    setPlaying(false);
    setEnded(true);
  };

  const handleReplay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    setEnded(false);
    v.play();
  };

  const goToNextVideo = () => {
    window.open(NEXT_VIDEO_URL, "_blank", "noopener,noreferrer");
  };

  // Auto-hide controls during playback
  const revealControls = useCallback(() => {
    setShowControls(true);
    if (hideControlsTimer.current) clearTimeout(hideControlsTimer.current);
    hideControlsTimer.current = setTimeout(() => {
      if (videoRef.current && !videoRef.current.paused) setShowControls(false);
    }, 2800);
  }, []);

  // Keyboard: space = play/pause, c = toggle CC, f = fullscreen, m = mute
  useEffect(() => {
    const onKey = (e) => {
      if (!started) return;
      if (e.code === "Space") {
        e.preventDefault();
        togglePlay();
      } else if (e.key.toLowerCase() === "c") {
        setCcOn((p) => !p);
      } else if (e.key.toLowerCase() === "f") {
        toggleFullscreen();
      } else if (e.key.toLowerCase() === "m") {
        toggleMute();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [togglePlay, started]);

  const progressPct = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="w-full max-w-7xl mx-auto" data-testid="ferum-landing-root">
      <div
        ref={containerRef}
        onMouseMove={revealControls}
        onMouseLeave={() => {
          if (playing) setShowControls(false);
        }}
        className="relative w-full aspect-video bg-black rounded-2xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(15,23,42,0.35)] ring-1 ring-slate-900/5 group"
        data-testid="video-container"
      >
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-contain bg-black"
          onPlay={() => {
            setPlaying(true);
            setStarted(true);
            setEnded(false);
            setLoadError("");
            revealControls();
          }}
          onPause={() => {
            setPlaying(false);
            setShowControls(true);
          }}
          onEnded={handleEnded}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onError={() => {
            const v = videoRef.current;
            const code = v?.error?.code;
            const map = {
              1: "Video load aborted.",
              2: "Network error while loading video.",
              3: "Video decoding error.",
              4: "Video format not supported by this browser.",
            };
            setLoadError(map[code] || "Unable to load video.");
          }}
          onClick={togglePlay}
          playsInline
          webkit-playsinline="true"
          x5-playsinline="true"
          preload="metadata"
          data-testid="video-element"
        >
          <source src="/ferum-intro.webm" type="video/webm" />
          <source src="/ferum-intro.mp4" type="video/mp4" />
        </video>

        {/* Error overlay */}
        {loadError && (
          <div
            className="absolute inset-0 flex items-center justify-center bg-black/85 text-center px-6"
            data-testid="video-error"
          >
            <div className="max-w-md">
              <p className="text-white text-base sm:text-lg font-medium mb-2">{loadError}</p>
              <p className="text-white/60 text-sm">Try refreshing the page.</p>
            </div>
          </div>
        )}

        {/* Big center play button (pre-play) */}
        {!started && (
          <button
            onClick={togglePlay}
            className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-black/10 via-black/20 to-black/40 transition hover:bg-black/30"
            data-testid="big-play-button"
            aria-label="Play video"
          >
            <span className="flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white/95 shadow-2xl backdrop-blur transform transition group-hover:scale-105">
              <Play className="w-8 h-8 sm:w-10 sm:h-10 text-slate-900 ml-1" fill="currentColor" />
            </span>
          </button>
        )}

        {/* Closed Captions overlay */}
        {ccOn && activeCaption && !ended && (
          <div
            className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-20 sm:bottom-24 px-4 max-w-[90%] text-center"
            data-testid="caption-overlay"
          >
            <span className="inline-block bg-black/80 text-white text-sm sm:text-base md:text-lg font-medium leading-snug px-3 py-2 rounded-md tracking-tight">
              {activeCaption}
            </span>
          </div>
        )}

        {/* End screen with Watch Next Video CTA */}
        {ended && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center bg-slate-950/85 backdrop-blur-sm gap-5 px-6"
            data-testid="end-screen"
          >
            <p className="text-slate-200 text-sm uppercase tracking-[0.25em]">Up Next</p>
            <h3 className="text-white text-2xl sm:text-3xl font-semibold text-center max-w-lg leading-tight">
              Continue the FERUM™ workflow walkthrough
            </h3>
            <div className="flex flex-col sm:flex-row items-center gap-3 mt-2">
              <button
                onClick={goToNextVideo}
                className="inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-slate-100 transition px-6 py-3 rounded-full font-semibold text-base shadow-lg"
                data-testid="watch-next-video-btn"
              >
                Watch Next Video
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={handleReplay}
                className="inline-flex items-center gap-2 bg-white/10 text-white hover:bg-white/20 transition px-5 py-3 rounded-full font-medium text-base ring-1 ring-white/20"
                data-testid="replay-btn"
              >
                <RotateCcw className="w-4 h-4" />
                Replay
              </button>
            </div>
          </div>
        )}

        {/* Controls bar */}
        {started && !ended && (
          <div
            className={`absolute left-0 right-0 bottom-0 px-3 sm:px-4 pb-3 pt-10 bg-gradient-to-t from-black/80 via-black/40 to-transparent transition-opacity duration-300 ${
              showControls ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
            data-testid="controls-bar"
          >
            {/* Progress */}
            <div
              className="group/progress relative h-1.5 w-full bg-white/25 rounded-full cursor-pointer mb-3"
              onClick={handleSeek}
              data-testid="progress-bar"
            >
              <div
                className="absolute top-0 left-0 h-full bg-white rounded-full"
                style={{ width: `${progressPct}%` }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover/progress:opacity-100 transition"
                style={{ left: `calc(${progressPct}% - 6px)` }}
              />
            </div>

            <div className="flex items-center justify-between gap-2 text-white">
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={togglePlay}
                  className="p-1.5 hover:bg-white/15 rounded-md transition"
                  aria-label={playing ? "Pause" : "Play"}
                  data-testid="play-pause-btn"
                >
                  {playing ? <Pause className="w-5 h-5" fill="currentColor" /> : <Play className="w-5 h-5" fill="currentColor" />}
                </button>

                <div className="flex items-center group/vol">
                  <button
                    onClick={toggleMute}
                    className="p-1.5 hover:bg-white/15 rounded-md transition"
                    aria-label={muted ? "Unmute" : "Mute"}
                    data-testid="mute-btn"
                  >
                    {muted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </button>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={muted ? 0 : volume}
                    onChange={handleVolume}
                    className="hidden sm:block w-0 group-hover/vol:w-24 group-hover/vol:ml-3 group-hover/vol:mr-2 transition-all duration-300 accent-white h-1"
                    aria-label="Volume"
                    data-testid="volume-slider"
                  />
                </div>

                <span className="text-xs sm:text-sm tabular-nums font-medium tracking-tight" data-testid="time-display">
                  {formatTime(currentTime)} <span className="text-white/60">/ {formatTime(duration)}</span>
                </span>
              </div>

              <div className="flex items-center gap-1 sm:gap-2">
                {/* CC button — YouTube style: bottom right with underline when active */}
                <button
                  onClick={() => setCcOn((p) => !p)}
                  className="relative p-1.5 hover:bg-white/15 rounded-md transition flex items-center"
                  aria-label="Toggle captions"
                  aria-pressed={ccOn}
                  data-testid="cc-toggle-btn"
                  title="Subtitles/closed captions (c)"
                >
                  <Subtitles className="w-5 h-5" />
                  <span
                    className={`absolute left-1.5 right-1.5 bottom-0.5 h-[2px] bg-white rounded-full transition-opacity ${
                      ccOn ? "opacity-100" : "opacity-0"
                    }`}
                  />
                </button>
                <button
                  onClick={toggleFullscreen}
                  className="p-1.5 hover:bg-white/15 rounded-md transition"
                  aria-label="Fullscreen"
                  data-testid="fullscreen-btn"
                >
                  <Maximize className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
